import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldShell, TextInput } from '@/lib/ui/Field';
import { Button } from '@/lib/ui/Button';

/**
 * Offline lat/lng picker for office coordinates (x-widget: 'geo').
 *
 * Mirrors the public KSA map (web/src/components/about/KsaMap + lib/geo/
 * ksaProjection): the same outline path and the same calibrated equirectangular
 * projection, so a pin dropped here lands exactly where the marker renders on
 * the site. Pure SVG + pointer events — NO map tiles, geocoding, or external
 * APIs. The two packages don't share code, so these constants are a deliberate
 * byte-identical copy of lib/geo/ksaProjection.
 *
 * Authoring: click (or drag) on the map to set the pin; or type the numbers
 * (e.g. pasted from Google Maps). The keyboard path is the number inputs; the
 * map is a pointer convenience.
 */

const VIEW = { w: 480, h: 380 };
const KSA_OUTLINE =
  'M 22 81 L 109 22 L 176 26 L 267 85 L 309 98 L 344 135 L 358 173 L 458 221 L 460 278 L 383 299 L 279 341 L 201 331 L 188 352 L 108 248 L 76 182 Z';

const X_PER_LNG = 20.6943;
const X_OFFSET = -693.01;
const Y_PER_LAT = -21.1409;
const Y_OFFSET = 699.32;
const BOUNDS = { minLat: 16, maxLat: 33, minLng: 34, maxLng: 56 };

const project = (lat: number, lng: number) => ({
  x: X_PER_LNG * lng + X_OFFSET,
  y: Y_PER_LAT * lat + Y_OFFSET,
});
const unproject = (x: number, y: number) => ({
  lng: (x - X_OFFSET) / X_PER_LNG,
  lat: (y - Y_OFFSET) / Y_PER_LAT,
});
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const round4 = (n: number) => Math.round(n * 1e4) / 1e4;

type GeoValue = { lat?: number; lng?: number } | undefined;

function numOf(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export function GeoPickerField({
  label,
  hint,
  value,
  onChange,
  required,
  error,
  disabled,
}: {
  label: string;
  hint?: string;
  value: GeoValue;
  onChange: (next: GeoValue) => void;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);

  const lat = numOf(value?.lat);
  const lng = numOf(value?.lng);
  const hasPin = lat !== undefined && lng !== undefined;
  const pin = hasPin ? project(lat, lng) : null;

  // Translate a pointer event to KSA WGS84, clamped to the Kingdom's box.
  const pointToCoord = (e: ReactPointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = ((e.clientX - rect.left) / rect.width) * VIEW.w;
    const y = ((e.clientY - rect.top) / rect.height) * VIEW.h;
    const { lat: rawLat, lng: rawLng } = unproject(x, y);
    return {
      lat: round4(clamp(rawLat, BOUNDS.minLat, BOUNDS.maxLat)),
      lng: round4(clamp(rawLng, BOUNDS.minLng, BOUNDS.maxLng)),
    };
  };

  const setFromPointer = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (disabled) return;
    const next = pointToCoord(e);
    if (next) onChange(next);
  };

  const setField = (key: 'lat' | 'lng', raw: string) => {
    const next: GeoValue = { lat, lng, [key]: raw === '' ? undefined : Number(raw) };
    if (next.lat === undefined && next.lng === undefined) onChange(undefined);
    else onChange(next);
  };

  return (
    <FieldShell
      label={t(label, { defaultValue: label })}
      required={required}
      error={error}
      hint={hint ? t(hint, { defaultValue: hint }) : undefined}
    >
      <div className="flex flex-col gap-sm">
        <div className="overflow-hidden rounded-sm border-[0.5px] border-hairline bg-canvas-paper">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEW.w} ${VIEW.h}`}
            className={`block h-auto w-full max-w-[420px] touch-none ${
              disabled ? 'opacity-60' : 'cursor-crosshair'
            }`}
            role="img"
            aria-label={t('fields.coordinates', { defaultValue: 'Coordinates' })}
            style={{ direction: 'ltr' }}
            onPointerDown={(e) => {
              if (disabled) return;
              e.currentTarget.setPointerCapture(e.pointerId);
              setFromPointer(e);
            }}
            onPointerMove={(e) => {
              if (disabled || e.buttons !== 1) return;
              setFromPointer(e);
            }}
          >
            <path
              d={KSA_OUTLINE}
              className="fill-sky-blue/15 stroke-accent-blue/70"
              strokeWidth={1.4}
              strokeLinejoin="round"
            />
            {pin ? (
              <g>
                <circle
                  cx={pin.x}
                  cy={pin.y}
                  r={9}
                  className="fill-none stroke-accent-blue/40"
                  strokeWidth={1}
                />
                <circle
                  cx={pin.x}
                  cy={pin.y}
                  r={5}
                  className="fill-accent-blue stroke-canvas-paper"
                  strokeWidth={2}
                />
              </g>
            ) : null}
          </svg>
        </div>

        <div className="flex flex-wrap items-end gap-md">
          <TextInput
            type="number"
            step="0.0001"
            label={t('fields.latitude', { defaultValue: 'Latitude' })}
            disabled={disabled}
            value={lat === undefined ? '' : String(lat)}
            onChange={(e) => setField('lat', e.target.value)}
            className="w-[8.5rem]"
          />
          <TextInput
            type="number"
            step="0.0001"
            label={t('fields.longitude', { defaultValue: 'Longitude' })}
            disabled={disabled}
            value={lng === undefined ? '' : String(lng)}
            onChange={(e) => setField('lng', e.target.value)}
            className="w-[8.5rem]"
          />
          {hasPin ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => onChange(undefined)}
            >
              {t('common.clear', { defaultValue: 'Clear' })}
            </Button>
          ) : null}
        </div>
      </div>
    </FieldShell>
  );
}
